import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, CircleDashed, FlaskConical, Trash2 } from "lucide-react";
import { api, shortDate } from "../lib/api";
import { useAuth, apiError } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";
import { Link } from "react-router-dom";
import { AutopilotPanel } from "../components/whop/AutopilotPanel";
import { ResolutionPanel } from "../components/whop/ResolutionPanel";


const Check = ({ ok, label, testid }) => (
  <p className="flex items-center gap-2 text-sm" data-testid={testid}>{ok ? <CheckCircle2 className="w-4 h-4 text-brand" /> : <CircleDashed className="w-4 h-4 text-white/35" />}{label}</p>
);

const Steps = () => (
  <div className="space-y-3 text-sm text-white/75" data-testid="whop-setup-steps">
    <p className="label-xs">Setting up Whop</p>
    <p>Every connection step (Whop key, webhook URL, signing secret, your website, the verification embed and a test purchase) lives in <b className="text-white">Connect &amp; Setup</b>, pre-filled with your production values and live checks.</p>
    <Link to="/app/connect" className="inline-block font-mono text-[11px] text-brand hover:underline" data-testid="whop-open-connect">OPEN CONNECT &amp; SETUP →</Link>
  </div>
);

const TestPanel = ({ reload }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState("");
  const run = async (kind) => {
    setBusy(kind);
    try {
      const pid = localStorage.getItem("aegis-whop-test-pay") || "";
      const { data } = await api.post("/whop/test-event", { kind, email, payment_id: kind.startsWith("dispute") ? pid : "" });
      if (kind === "payment.succeeded") localStorage.setItem("aegis-whop-test-pay", data.event.data.id);
      toast.success(kind === "payment.succeeded" ? `Test payment ${data.event.data.id} received` : `Test case opened: ${data.result.dispute_ids?.[0] || data.result.dispute_id || "processed"}`);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const clear = async () => { const { data } = await api.delete("/whop/test-data?dry_run=false"); toast.success(`Cleared ${data.transactions} test payments and ${data.disputes} test disputes`); reload(); };
  return (
    <div className="panel p-5" data-testid="whop-test-panel">
      <p className="label-xs flex items-center gap-2"><FlaskConical className="w-3.5 h-3.5 text-brand" /> Test the flow (labelled TEST, nothing goes to Whop)</p>
      <p className="text-sm text-white/60 mt-2">1) Send a test payment using your own email. 2) Open the verification link with that email and record yourself. 3) Send a test dispute and see the video attached automatically.</p>
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your email for the test buyer" className="mt-3 w-full bg-field border border-fieldline px-3 py-2 text-sm focus:outline-none focus:border-brand" data-testid="whop-test-email" />
      <div className="flex flex-wrap gap-2 mt-3">
        <NeonButton onClick={() => run("payment.succeeded")} disabled={!!busy} data-testid="whop-test-payment-btn">Send test payment</NeonButton>
        <NeonButton variant="ghost" onClick={() => run("dispute.created")} disabled={!!busy} data-testid="whop-test-dispute-btn">Send test dispute</NeonButton>
        <NeonButton variant="ghost" onClick={() => run("dispute.warning")} disabled={!!busy} data-testid="whop-test-warning-btn">Send test warning</NeonButton>
        <NeonButton variant="ghost" onClick={() => run("resolution.created")} disabled={!!busy} data-testid="whop-test-resolution-btn">Send test resolution case</NeonButton>
        <NeonButton variant="danger" onClick={clear} data-testid="whop-clear-test-btn"><Trash2 className="w-3 h-3" /> Clear test data</NeonButton>
      </div>
    </div>
  );
};

export default function WhopConnect() {
  const { isOwner } = useAuth();
  const [s, setS] = useState(null);
  const [tick, setTick] = useState(0);
  const load = useCallback(() => { setTick((t) => t + 1); return api.get("/whop/status").then((r) => setS(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  if (!s) return <p className="font-mono text-xs text-white/40">loading…</p>;
  return (
    <div data-testid="whop-page">
      <PageHeader eyebrow="// integration · whop" title="Whop Connection" />
      <div className="grid lg:grid-cols-[1.3fr_1fr] gap-6">
        <div className="panel p-6"><Steps /></div>
        <div className="space-y-6">
          <div className="panel p-5 space-y-3" data-testid="whop-status">
            <p className="label-xs">Connection status · <span className={s.mode === "live" ? "text-brand" : "text-amber-300"} data-testid="whop-mode">{s.mode === "live" ? "LIVE" : "TEST MODE"}</span></p>
            <Check ok={s.webhook_secret} label="Webhook signing secret activated" testid="whop-check-secret" />
            <Check ok={s.api_key} label="Company API key activated (customer lookup + evidence push)" testid="whop-check-key" />
            <Check ok={!!s.last_event} label={s.last_event ? `Last event: ${s.last_event.type} · ${shortDate(s.last_event.received_at)}` : "No live events received yet"} testid="whop-check-event" />
          </div>
          {isOwner && <TestPanel reload={load} />}
        </div>
      </div>
      <div className="mt-6 grid xl:grid-cols-2 gap-6"><ResolutionPanel key={`r${tick}`} apiKey={s.api_key} /><AutopilotPanel key={tick} /></div>
    </div>
  );
}
